import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Star } from 'lucide-react';

import { dashboardApi, type TrainerFeedbackItem } from '@/lib/api/admin-dashboard';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatTimestamp } from '@/lib/date';

export const Route = createFileRoute('/admin/trainer-feedback')({
  component: TrainerFeedbackPage,
});

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-4 w-4 ${
            n <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'fill-transparent text-muted-foreground/30'
          }`}
        />
      ))}
    </span>
  );
}

function TrainerFeedbackPage() {
  const [trainerId, setTrainerId] = useState('');
  const [batchId, setBatchId] = useState('');

  const { data: summary } = useQuery({
    queryKey: ['admin', 'trainer-feedback', 'summary'],
    queryFn: dashboardApi.getTrainerFeedbackSummary,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'trainer-feedback', { trainerId, batchId }],
    queryFn: () => dashboardApi.getTrainerFeedback({ trainer_id: trainerId, batch_id: batchId }),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Trainer Feedback</h1>
        <p className="text-sm text-muted-foreground">
          Student feedback about trainers, per topic. Visible to admins only — trainers and students cannot see it.
        </p>
      </div>

      {/* Per-trainer averages */}
      {summary && summary.trainers.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {summary.trainers.map((t) => (
            <Card key={t.trainer_id}>
              <CardContent className="p-4">
                <div className="truncate text-sm font-semibold">{t.trainer_name || 'Unknown'}</div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-amber-500">{t.avg_rating.toFixed(1)}</span>
                  <span className="text-xs text-muted-foreground">/ 5</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <Stars rating={t.avg_rating} />
                  <span className="text-xs text-muted-foreground">{t.count} review{t.count === 1 ? '' : 's'}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={trainerId}
          onChange={(e) => setTrainerId(e.target.value)}
          className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">All trainers</option>
          {summary?.trainers.map((t) => (
            <option key={t.trainer_id} value={t.trainer_id}>{t.trainer_name || 'Unknown'}</option>
          ))}
        </select>
        <select
          value={batchId}
          onChange={(e) => setBatchId(e.target.value)}
          className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">All batches</option>
          {summary?.batches.map((b) => (
            <option key={b.batch_id} value={b.batch_id}>{b.batch_name || 'Unknown'}</option>
          ))}
        </select>
        {(trainerId || batchId) && (
          <button
            onClick={() => { setTrainerId(''); setBatchId(''); }}
            className="text-sm text-primary hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">Failed to load feedback.</p>
      ) : !data || data.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No feedback{(trainerId || batchId) ? ' matches these filters' : ' submitted yet'}.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">Rating</th>
                    <th className="px-4 py-3 font-semibold">Trainer</th>
                    <th className="px-4 py-3 font-semibold">Topic</th>
                    <th className="px-4 py-3 font-semibold">Batch</th>
                    <th className="px-4 py-3 font-semibold">Student</th>
                    <th className="px-4 py-3 font-semibold">Comment</th>
                    <th className="px-4 py-3 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((f: TrainerFeedbackItem) => (
                    <tr key={f.id} className="border-b align-top last:border-0">
                      <td className="whitespace-nowrap px-4 py-3"><Stars rating={f.rating} /></td>
                      <td className="px-4 py-3 font-medium">{f.trainer_name || '—'}</td>
                      <td className="px-4 py-3">{f.topic_title || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{f.batch_name || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{f.student_name || '—'}</td>
                      <td className="max-w-xs px-4 py-3 text-muted-foreground">
                        {f.comment ? <span className="whitespace-pre-wrap">{f.comment}</span> : <span className="italic opacity-60">No comment</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                        {formatTimestamp(f.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
